import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { connect } from 'react-redux';
import MetisMenu from 'react-metismenu';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setOffcanvas } from '../../actions/settingsAction';
import buildMetisMenu from "./metisMenu";
import DefaultLink from './DefaultLink';
import MyOpinionLogo from '../../assets/images/MyOpinion_Logo_Head.png';
import httpService from '../../services/http.service';
import * as jwtService from '../../services/jwt.service';
import { firestoreDB } from '../../services/Firebase/firebaseConfig';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

/**
 * Badges du menu (nombre d'éléments à traiter), affichés si le JWT donne le module concerné :
 * - Signalements en attente : `GET reports/pending-count` ;
 * - Validation d'identité en attente : `meta.total` de `GET verifications?statut=Pending` ;
 * rafraîchis toutes les minutes et après chaque décision (événements `reports:changed`, `verifications:changed`).
 * - Lives en cours : écoute Firestore en temps réel (même requête que la page Lives).
 */
const BADGE_POLL_MS = 60000;
const POLLED_BADGES = [
	{ to: '/reports', permission: 'reports', event: 'reports:changed',
		load: async () => (await httpService.get('/reports/pending-count')).data?.data?.pending || 0 },
	{ to: '/verifications', permission: 'verifications', event: 'verifications:changed',
		load: async () => (await httpService.get('/verifications', { params: { statut: 'Pending', page: 1 } })).data?.meta?.total || 0 },
];

const allowed = (permission) => {
	const permissions = (jwtService.get() || {}).permissions;
	return !Array.isArray(permissions) || permissions.includes(permission);
};

const useMenuBadges = (user) => {
	const [badges, setBadges] = useState({});
	const setBadge = (to, value) => setBadges((state) => (state[to] === value ? state : { ...state, [to]: value }));

	useEffect(() => {
		if (!user) {
			setBadges({});
			return undefined;
		}
		let alive = true;
		const cleanups = [];

		POLLED_BADGES.filter((badge) => allowed(badge.permission)).forEach((badge) => {
			const load = async () => {
				try {
					const value = await badge.load();
					if (alive) setBadge(badge.to, value);
				} catch (e) {
					// badge facultatif : une erreur ne doit pas gêner la navigation
				}
			};
			load();
			const timer = setInterval(load, BADGE_POLL_MS);
			window.addEventListener(badge.event, load);
			cleanups.push(() => { clearInterval(timer); window.removeEventListener(badge.event, load); });
		});

		if (allowed('agoras')) {
			try {
				const liveQuery = query(collection(firestoreDB, 'myopinion', 'lives', 'channels'), where('live_finished', '==', false));
				cleanups.push(onSnapshot(liveQuery, (snapshot) => alive && setBadge('/lives', snapshot.size), () => null));
			} catch (e) {
				// Firebase non configuré : pas de badge
			}
		}

		return () => {
			alive = false;
			cleanups.forEach((cleanup) => cleanup());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);
	return badges;
};

/**
 * react-metismenu garde le composant de lien de son premier rendu : les compteurs lui parviennent donc par
 * un contexte (lu par chaque lien, qui se redessine quand ils changent) et non par une prop.
 */
const MenuBadgesContext = createContext({});
const BadgedLink = (props) => {
	const badges = useContext(MenuBadgesContext);
	return <DefaultLink itemProps={props} badge={badges[props.to]} />;
};

/**
 * Barre latérale : fixe sur ordinateur (réductible en icônes, elle se déplie au survol), tiroir sur mobile
 * (fermé au clic sur le fond, à la touche Échap et à chaque changement de page).
 */
const Menu = ({ user, offcanvas, setOffcanvas, location }) => {
	const { t } = useTranslation();
	const menuContent = useMemo(() => buildMetisMenu(t), [user, t]);
	const pathname = location?.pathname;
	const badges = useMenuBadges(user);

	// Tiroir mobile : fermé après navigation.
	useEffect(() => {
		if (offcanvas) setOffcanvas(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	// Échap ferme le tiroir ; repasser en grand écran le referme aussi.
	useEffect(() => {
		const onKey = (e) => { if (e.key === 'Escape') setOffcanvas(false); };
		const onResize = () => { if (window.innerWidth >= 992) setOffcanvas(false); };
		window.addEventListener('keydown', onKey);
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('keydown', onKey);
			window.removeEventListener('resize', onResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sous-menus : ouvert uniquement si l'un de ses liens est la page courante.
	const containerClass = (e) => {
		if (e.itemId) {
			const active = e.items.some((menuItem) => menuItem.to === pathname);
			return active ? '' : 'collapse';
		}
		return e.visible ? 'collapse' : 'metismenu';
	};

	return (
		<>
			<div className="app-overlay" onClick={() => setOffcanvas(false)} aria-hidden="true" />
			<aside id="app-sidebar" className="app-sidebar" aria-label={t('Menu')}>
				<div className="app-sidebar__brand">
					<Link to="/" className="app-sidebar__logo">
						<img src={MyOpinionLogo} alt="" />
						<span className="app-sidebar__title">
							<strong>{process.env.REACT_APP_APP_NAME}</strong>
							<small>{t('Espace administrateur')}</small>
						</span>
					</Link>
					<button type="button" className="app-sidebar__close" onClick={() => setOffcanvas(false)} aria-label={t('Fermer')}>
						<i className="fa fa-times" />
					</button>
				</div>
				<MenuBadgesContext.Provider value={badges}>
				<nav className="app-sidebar__nav">
					<MetisMenu
						content={menuContent}
						noBuiltInClassNames={true}
						classNameItemActive="active"
						classNameContainer={containerClass}
						classNameContainerVisible="in"
						iconNamePrefix=""
						LinkComponent={BadgedLink}
					/>
				</nav>
				</MenuBadgesContext.Provider>
			</aside>
		</>
	);
};

const mapStateToProps = state => ({
	offcanvas: state.settings.offcanvas,
	user: state.auth.user,
});

const mapDispatchToProps = dispatch => ({
	setOffcanvas: (e) => dispatch(setOffcanvas(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Menu);
